import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Archive,
  Scale,
  ShieldCheck,
  FileSearch,
  Lock,
  Gavel,
  Globe2,
  ClipboardList,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Archive,
    title: 'WCC Records component is the engine',
    detail:
      'WebCenter Content Records (the records management add-on to WCC) administers retention schedules, categories, dispositions, freezes (legal holds), and audit-trail records. The schedule library and category metadata are configuration inside WCC — not a separate database.',
  },
  {
    icon: Scale,
    title: 'Retention rules carry regulatory weight',
    detail:
      'A typical AP install carries SOX (7-year financial records), IRS (3-7 years for tax-backing documents), GDPR / state privacy retention limits, HIPAA (6 years for healthcare), and industry-specific schedules (K-12 student-records statutes, public-sector retention codes, transportation safety records).',
  },
  {
    icon: ShieldCheck,
    title: 'Audit trail is non-negotiable',
    detail:
      'WCC Records maintains an immutable audit trail across every record event — create, classify, freeze, unfreeze, transfer, accession, destroy. Auditors expect that trail to survive any migration intact, with timestamps and user attribution preserved.',
  },
  {
    icon: Gavel,
    title: 'Legal hold is the highest-stakes path',
    detail:
      'Freeze (legal hold) operations must be preserved across the migration boundary. A held invoice that loses its freeze status during cutover is a sanctionable spoliation event in active litigation, not a configuration nit.',
  },
];

const RETENTION_ANATOMY = [
  {
    step: '1',
    title: 'Retention schedules',
    body:
      'WCC Records implements one or more retention schedules — typically a corporate schedule plus jurisdictional sub-schedules. Each schedule contains categories that map document metadata (DocType = APInvoice, FiscalYear, BusinessUnit, Jurisdiction) to a retention period and a disposition.',
  },
  {
    step: '2',
    title: 'Categories and triggers',
    body:
      'Each category specifies a triggering event (invoice paid date, fiscal year close, contract termination, last-activity date) and a retention period measured from that trigger. Cutoff instructions, periodic reviews, and vital-records flags ride on the category definition.',
  },
  {
    step: '3',
    title: 'Dispositions',
    body:
      'Standard dispositions in WCC Records: transfer (move to lower-cost storage), accession (transfer to a designated archive — common for public-sector and government), destroy (controlled destruction with audit-trail record), and review (human review before next step). Disposition workflows can include approval routing and notification.',
  },
  {
    step: '4',
    title: 'Legal hold (freeze) workflow',
    body:
      'Records under freeze are exempt from disposition regardless of schedule. WCC Records supports freeze reasons, freeze owners, and selective freeze/unfreeze across record sets identified by query. Customizations often include integration with legal-matter systems and notifications.',
  },
  {
    step: '5',
    title: 'Audit trail and reporting',
    body:
      'Every classification, freeze, hold, and disposition event writes to the audit trail. Standard reports support retention-schedule certification, legal-hold inventories, and disposition history — all of which are what auditors actually open during compliance reviews.',
  },
];

const PATH_OPTIONS = [
  {
    label: 'Path A+ — Upgrade to 14c',
    summary: 'Retention model carried forward verbatim',
    body:
      'WebCenter Content 14c retains the Records component and its retention-schedule architecture. Schedules, categories, dispositions, freezes, and the audit trail migrate forward as WCC configuration via the Oracle Upgrade Assistant out-of-place domain directory pattern. Customizations around legal-hold workflow and disposition routing re-deploy with validation. The retention model itself does not need to be redesigned.',
  },
  {
    label: 'Path B — Modernize off WebCenter',
    summary: 'Retention model re-implemented on the target platform with audit-trail preservation',
    body:
      'Modernizing the AP layer means the retention rules need to be re-implemented on the new platform — schedule library rebuilt, categories mapped, dispositions reconfigured, legal-hold workflow recreated, and the historical audit trail preserved either as an immutable export or carried into the new system. This is its own workstream inside a modernization project. Done well, it is also an opportunity to consolidate the retention model and retire schedules that no longer match current regulation.',
  },
  {
    label: 'Path B+ — Hybrid (recommended for high-compliance scenarios)',
    summary: 'Modernize AP processing, retain WCC as the regulated archive',
    body:
      'AP processing modernizes onto an AI-driven platform; WebCenter Content remains the system of record for the regulated archive. Retention schedules, dispositions, and legal-hold workflow stay where the auditors already understand them. The 12c → 14c upgrade still applies to the WCC archive footprint, but the scope shrinks to records management without the AP processing surface.',
  },
];

const SCENARIOS = [
  {
    icon: ClipboardList,
    title: 'Compliance team wants written confirmation that audit trail survives migration',
    detail:
      'A common ask from internal audit or external auditors before sign-off on any modernization plan: a documented mapping showing every audit-trail event type in the source system has an equivalent in the destination, with the historical record preserved. We deliver this as an artifact, not a verbal assurance.',
  },
  {
    icon: Gavel,
    title: 'Legal hold workflow customizations need preservation',
    detail:
      'Many implementations carry custom legal-hold workflow — integration with the legal department\'s matter-management system, custom freeze-reason taxonomies, automated freeze on litigation-trigger criteria, custodial notifications. These customizations need to be inventoried and translated forward, not lost in the migration.',
  },
  {
    icon: Globe2,
    title: 'Multi-jurisdiction retention (US + EU GDPR + AU privacy)',
    detail:
      'Customers running internationally carry overlapping retention regimes. WCC Records typically implements this with jurisdictional category sub-schedules. Any forward path needs to preserve the jurisdictional logic explicitly — a single corporate schedule that ignores GDPR right-to-erasure or AU Privacy Act limits is a compliance regression, not a simplification.',
  },
  {
    icon: FileSearch,
    title: 'Adjacent records modernization wanting consolidation',
    detail:
      'AP records often share a WCC Records instance with HR records, contracts, T&E receipts, and operational documents. The December 2026 decision is a natural moment to consolidate the retention model across these adjacent record categories — or to deliberately separate them by platform. Either choice needs to be made deliberately, not by default.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    eyebrow: 'Tier 1 · Retention Inventory',
    title: 'Fixed-fee retention rule inventory',
    body:
      'Andrew reads your existing WCC Records configuration directly and produces a structured inventory: every schedule, every category, every disposition, every freeze policy, every customization, every audit-trail event type. Output is yours to keep regardless of which forward path you choose.',
  },
  {
    eyebrow: 'Tier 2 · Migration Mapping',
    title: 'Source-to-destination retention mapping',
    body:
      'Fixed-fee mapping document covering the chosen forward path (A+, B, or B+). Schedules mapped to destination constructs, dispositions translated, legal-hold workflow re-specified, audit-trail preservation strategy documented, jurisdictional logic preserved. Used as input to the upgrade or modernization plan.',
  },
  {
    eyebrow: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through the compliance window',
    body:
      'Monthly retainer with Andrew, sized for the customer working through a 6-12 month decision that requires sustained compliance, legal, and internal-audit engagement. Strategic guidance on schedule consolidation, jurisdictional design, legal-hold preservation, and auditor-readiness artifacts.',
  },
  {
    eyebrow: 'Tier 4 · Forward Deployed Engineering',
    title: 'Custom retention rule archeology and re-implementation',
    body:
      'For implementations carrying years of accreted customization — bespoke disposition workflows, custom freeze taxonomies, embedded jurisdictional logic, undocumented category overrides — Andrew embeds with your team to read the running configuration, reconstruct intent, and re-implement on the destination platform. Project-based, founder-led, scoped to a defined outcome.',
  },
];

const FAQS = [
  {
    q: 'Does the retention model survive a 12c to 14c upgrade?',
    a: 'Yes — the retention model carries forward verbatim on a 12c to 14c upgrade. WebCenter Content 14c retains the Records component and its retention-schedule architecture. Schedules, categories, dispositions, freezes, and the audit trail migrate forward as WCC configuration via the Oracle Upgrade Assistant out-of-place domain directory pattern. Customizations around legal-hold workflow and disposition routing re-deploy with validation against 14c APIs, and the retention model itself does not need to be redesigned.',
  },
  {
    q: 'What happens specifically to the WebCenter Content Records component on upgrade?',
    a: 'WCC Records is upgraded as part of the standard 12c to 14c domain upgrade. WCC Records is a packaged WebCenter Content component (formerly Universal Records Management, URM), and its configuration — the schedule library, category definitions, disposition rules, freeze policies, and audit-trail database — is part of the WCC domain and database. Custom components that extend Records (custom disposition actions, freeze integrations) re-deploy in the new 14c domain and are validated against any Records-component API changes between releases.',
  },
  {
    q: 'How is legal hold preserved across migration?',
    a: 'Legal hold is preserved verbatim on a 14c upgrade and via an export-replay-verify sequence on a modernization (Path B). Freeze (legal hold) status is metadata on the affected records, plus the freeze definition itself in WCC Records. Across a modernization the freeze inventory is exported as an authoritative snapshot, replayed in the destination, and verified item-by-item before cutover — the audit log of the freeze export, replay, and verification becomes part of the compliance record for any active matter.',
  },
  {
    q: 'How does multi-jurisdiction retention (US + EU + AU) carry forward?',
    a: 'Multi-jurisdiction retention carries forward verbatim on a 14c upgrade and must be re-implemented explicitly on a modernization path. WCC Records typically implements multi-jurisdiction retention via jurisdictional category sub-schedules, with classification driven by metadata (BusinessUnit, Region, DataSubjectJurisdiction), and the category structure migrates verbatim on a 14c upgrade. On a modernization path, flattening to a single corporate schedule is a compliance regression where GDPR storage-limitation or state privacy law requires shorter retention than the US default.',
  },
  {
    q: 'How is the audit trail preserved?',
    a: 'The audit trail is preserved through the standard database upgrade on a 14c path, and via immutable export or one-to-one event-type mapping on a modernization path. On a 14c upgrade the audit trail is part of the WCC database. On a modernization path the historical audit trail is preserved either as an immutable export (PDF/A archive plus structured data export, signed and timestamped) or carried forward into the destination system\'s audit log if a one-to-one event-type mapping exists. Most customers carry forward the events going forward and retain the historical export as the regulated archive for the legacy period.',
  },
  {
    q: 'What about IRS and SOX records retention specifically?',
    a: 'SOX requires 7-year retention with destruction prohibited during audits, and IRS requires 3-7 years depending on circumstance — both regimes are preserved at the schedule level on a 14c upgrade and re-specified explicitly on a modernization path. SOX (Section 802) requires 7-year retention of audit work papers and supporting records, and proscribes destruction during pending or anticipated audits — implemented in WCC Records as a category with a 7-year retention from period-close plus a freeze policy keyed to audit status. IRS requires 3 years generally and 7 years for substantial-understatement or worthless-securities scenarios, with longer retention for property basis. The SOX destruction-prohibition trigger needs to be re-specified explicitly on any modernization path.',
  },
  {
    q: 'How does the WCC retention model map to cloud-native records management?',
    a: 'The mapping from a WCC Records schedule to cloud-native records management is not one-to-one — disposition workflow, freeze taxonomies, and audit-trail event types differ. Cloud-native records management offerings (Oracle Content Management classifications, AWS S3 Object Lock with retention modes, Azure Blob legal hold and immutability policies) implement retention as object-level metadata plus immutability enforcement at the storage layer. We map the model construct by construct as part of the migration plan, and surface where re-implementation is required versus where direct translation works.',
  },
  {
    q: 'What is the typical timeline for a retention modernization workstream?',
    a: 'Retention inventory takes 2-4 weeks, migration mapping another 3-6 weeks, and Path B re-implementation typically runs 3-6 months as part of the larger modernization project. Inventory timing depends on customization depth. Re-implementation and audit-trail preservation windows extend further where legal-hold workflow customizations or multi-jurisdiction logic are deep. On a Path A+ upgrade the retention-specific work folds into the 6-12 month 14c upgrade timeline with no additional schedule risk.',
  },
];

const WebCenterRecordsRetentionModernization = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter Records Retention Modernization — Preserve Audit Posture Through Migration',
    description:
      'Records retention rules are the compliance backbone of every WebCenter AP install — invoice retention periods, legal hold support, audit trail preservation. Any forward path through the December 2026 cliff must preserve the retention model. EZ Cloud delivers retention rule inventory, 14c migration, and modernization mapping to cloud-native records management.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-records-retention-modernization',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Records' },
      { '@type': 'Thing', name: 'WebCenter Records Management' },
      { '@type': 'Thing', name: 'Records Retention' },
      { '@type': 'Thing', name: 'Audit Trail' },
      { '@type': 'Thing', name: 'Legal Hold' },
      { '@type': 'Thing', name: 'SOX Compliance' },
      { '@type': 'Thing', name: 'IRS Records Retention' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter Records Retention Modernization — Preserve Audit Posture Through Migration | EZ Cloud"
        description="Records retention rules are the compliance backbone of every WebCenter AP install — invoice retention periods, legal hold support, audit trail preservation. Any forward path through the December 2026 cliff must preserve the retention model. EZ Cloud delivers retention rule inventory, 14c migration, and modernization mapping to cloud-native records management."
        canonical="https://ezcloud.ai/solutions/webcenter-records-retention-modernization"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter Records Retention Modernization',
            url: 'https://ezcloud.ai/solutions/webcenter-records-retention-modernization',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <ShieldCheck className="w-4 h-4" aria-hidden />
              Records retention · Compliance · Audit trail
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter records retention modernization
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Preserve the retention rules, audit trail, and legal hold support through Dec 2026.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Records retention is the compliance backbone of every WebCenter AP install. Any
              forward path through Dec 2026 must preserve the retention rules, audit trail, and
              legal hold support — not as a checkbox, but as the core artifact compliance and
              internal audit will open during the migration.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('records-retention-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Records Retention Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Records retention in WebCenter, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Records retention is the rule layer that governs how long every AP invoice — and
                  every supporting document attached to it — is kept, when it can be destroyed, and
                  under what circumstances it must be frozen against destruction.
                </strong>{' '}
                In an Oracle WebCenter AP install, those rules live in the WebCenter Content Records
                component: a schedule library of categories, retention periods, disposition actions,
                freeze (legal hold) policies, and an immutable audit trail.
              </p>
              <p>
                Every regulatory regime that touches AP flows through this layer.{' '}
                <strong className="text-gray-900">
                  SOX (Section 802) requires 7-year retention of audit-supporting records and
                  prohibits destruction during pending audits. IRS rules require 3-7 years
                  depending on circumstance. GDPR and state privacy laws impose storage-limitation
                  ceilings. HIPAA requires 6 years for healthcare. K-12, public sector, and
                  transportation each carry their own statutory schedules.
                </strong>{' '}
                These regimes are not abstract — they are categories in the schedule library, each
                with a retention period, a trigger, and a disposition.
              </p>
              <p>
                Any forward path through the December 2026 cliff has to preserve this layer. A 14c
                upgrade carries the schedule library forward verbatim as WCC configuration. A
                modernization to a different platform requires re-implementing the schedule library,
                translating dispositions, recreating the freeze workflow, and preserving the
                historical audit trail — a workstream in its own right. This page covers both paths
                and the hybrid (modernize AP processing, retain WCC as the regulated archive).
              </p>
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What records retention looks like in WebCenter
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four anchors of the WebCenter retention layer
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the constructs auditors, compliance officers, and litigation counsel actually
              ask about. Every forward path needs to map cleanly to each of them.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Retention anatomy */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The anatomy of WCC Records
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              How retention is actually implemented in WebCenter Content
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Five layers compose the retention model. Each layer needs to be inventoried, mapped,
              and validated on any forward path — and each layer is where customization
              accumulates over years of audit findings and legal matters.
            </p>
            <div className="space-y-5">
              {RETENTION_ANATOMY.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's at stake at Dec 2026 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What's at stake at December 2026
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
              The retention rules are configuration. They are also load-bearing.
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                The retention schedules in WCC Records are not code. They are configuration —
                schedule definitions, category metadata, disposition rules, freeze policies. As
                configuration, they{' '}
                <strong className="text-gray-900">survive a 14c upgrade verbatim</strong> through
                the Oracle Upgrade Assistant out-of-place domain directory pattern. A team running
                Path A+ (upgrade to 14c) does not need to redesign the retention model.
              </p>
              <p>
                A team running Path B (modernize off WebCenter) faces a different question. The
                schedule library, the category structure, the disposition workflow, and the
                freeze taxonomy all have to be{' '}
                <strong className="text-gray-900">re-implemented on the destination platform</strong>{' '}
                — and the historical audit trail has to be preserved in a way that satisfies internal
                audit and external auditors. This is a real workstream, not a paragraph in the
                project plan. It is also a chance to consolidate years of accumulated schedules and
                retire categories that no longer match current regulation.
              </p>
              <p>
                Either way, the retention model is one of the constructs that{' '}
                <strong className="text-gray-900">
                  compliance, legal, and internal audit will inspect before any cutover gets signed
                </strong>
                . Surface it early, map it explicitly, and the rest of the migration moves faster.
              </p>
            </div>
          </div>
        </section>

        {/* Three forward paths */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Three forward paths through the retention lens
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              A+, B, B+ — how each path treats the retention layer
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The retention lens is one of the most useful lenses for choosing between the forward
              paths. Each path has a distinct posture on the retention model, the legal-hold
              workflow, and audit-trail continuity.
            </p>
            <div className="space-y-5">
              {PATH_OPTIONS.map((p) => (
                <Card key={p.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {p.label}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{p.summary}</h3>
                  <p className="text-gray-700 leading-relaxed">{p.body}</p>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full 12c end-of-support decision frame
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* K-12 customer example */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              A live customer example
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
              K-12 records retention deployment on Oracle EBS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                EZ Cloud is engaged on a records retention deployment for a US K-12 customer
                running Oracle EBS — an expansion of an existing AP automation footprint into the
                records management module. The work covers retention schedule design against the
                applicable state K-12 records retention code, category structure for AP and
                adjacent finance records, disposition workflow with approval routing, and audit
                trail integration with the customer's existing compliance posture.
              </p>
              <p>
                The deployment illustrates the pattern: records retention is rarely a standalone
                project, but it is often the artifact that unlocks the next phase of modernization
                — because compliance and internal audit are reassured that the retention model is
                explicit, documented, and operational on the destination platform.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Compare with the 14c upgrade path
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where customers are on the retention question
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the scenarios that come up most often on Decision Calls about WebCenter
              records retention through the December 2026 window.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement options */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on retention modernization
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement options across standard consulting and forward deployed engineering
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level WebCenter expertise that powers EZ Cloud's
              modernization assessments applies to retention rule inventory, mapping, and
              re-implementation work.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.eyebrow}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, including records retention
              considerations, with stack-specific guidance, eight diagnostic questions, and a
              vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on WebCenter records retention modernization
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your retention model
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. Walk through your schedule library, legal-hold
              workflow, jurisdictional logic, and audit-trail posture in the context of your
              specific implementation and the December 2026 window.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('records-retention-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Records Retention Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterRecordsRetentionModernization;
